import { afterRenderEffect, ChangeDetectionStrategy, Component, computed, type ElementRef, inject, signal, viewChild } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { NgpMenuTrigger } from 'ng-primitives/menu';
import { heroArrowDown, heroArrowTopRightOnSquare, heroArrowUturnLeft, heroAtSymbol, heroEllipsisHorizontal, heroInformationCircle, heroLockClosed, heroPencilSquare } from '@ng-icons/heroicons/outline';
import { environment } from '../../../../../environments/environment';
import { LinkifyPipe, UiBadge, UiButton, UiEmptyState, UiIconButton, UiMenuItem, UiMenuPanel, UiPanelCard, UiSkeletonGrid } from '../../../../shared/ui';
import { ConversationFiltersStore } from '../../data/conversation-filters.store';
import { ConversationsStore } from '../../data/conversations.store';
import { conversationStatusLabel, quotedReplySenderLabel } from '../../i18n/conversation-labels';
import { channelKindFromLabel } from '../../media/media-constraints';
import type { ConversationView, ThreadMessage } from '../../models/conversation-view.types';
import { ChannelIconComponent } from '../../../../shared/ui/ui-channel-icon';
import { deliveryIndicator, type DeliveryIndicator } from './delivery-indicator';
import { MessageAttachmentsComponent } from './message-attachments';
import { MessageComposerComponent } from './message-composer';
import { ThreadHeaderActionsComponent } from './thread-header-actions';
import { isNearBottom, shouldAutoScroll } from './thread-scroll';
import { buildTimeline, type DayGroup, type TimelineEntry } from './thread-timeline';

type UiBadgeTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'accent';

/** Conversation thread shell: header (+ action cluster), message history and composer. */
@Component({
  selector: 'app-conversation-thread',
  imports: [
    NgIcon,
    NgpMenuTrigger,
    LinkifyPipe,
    UiBadge,
    UiButton,
    UiEmptyState,
    UiIconButton,
    UiMenuItem,
    UiMenuPanel,
    UiPanelCard,
    UiSkeletonGrid,
    ChannelIconComponent,
    MessageAttachmentsComponent,
    MessageComposerComponent,
    ThreadHeaderActionsComponent,
  ],
  providers: [provideIcons({ heroArrowDown, heroArrowTopRightOnSquare, heroArrowUturnLeft, heroAtSymbol, heroEllipsisHorizontal, heroInformationCircle, heroLockClosed, heroPencilSquare })],
  templateUrl: './thread-view.html',
  styleUrls: ['./thread-view.css', '../conversation-atoms.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ThreadViewComponent {
  protected readonly store = inject(ConversationsStore);
  private readonly filters = inject(ConversationFiltersStore);
  protected readonly conversation = computed(() => this.store.selectedConversation());
  protected readonly timeline = computed<DayGroup[]>(() =>
    buildTimeline(this.store.selectedMessages(), this.store.selectedNotes()),
  );

  private readonly scroller = viewChild<ElementRef<HTMLElement>>('scroller');
  protected readonly isAtBottom = signal(true);

  private previousConversationId = '';

  constructor() {
    // After each render, snap to the bottom when the conversation changed or the user was already
    // following the bottom (new message, optimistic send, or a force-reload that rebuilds the list
    // and would otherwise reset scroll to the top). Reading scroll geometry post-render is safe;
    // nothing writes signals the template reads. Depending on timeline() keeps it reactive.
    afterRenderEffect(() => {
      const element = this.scroller()?.nativeElement;
      const conversationId = this.store.selectedConversationId();
      this.timeline();
      if (!element) {
        return;
      }
      const conversationChanged = conversationId !== this.previousConversationId;
      this.previousConversationId = conversationId;

      if (shouldAutoScroll({ conversationChanged, wasAtBottom: this.isAtBottom() })) {
        element.scrollTop = element.scrollHeight;
        this.isAtBottom.set(true);
      }
    });
  }

  onScroll(): void {
    const element = this.scroller()?.nativeElement;
    if (element) {
      this.isAtBottom.set(isNearBottom(element));
    }
  }

  scrollToBottom(): void {
    const element = this.scroller()?.nativeElement;
    if (element) {
      element.scrollTo({ top: element.scrollHeight, behavior: 'smooth' });
      this.isAtBottom.set(true);
    }
  }

  openNote(noteId: string): void {
    this.filters.focusNote(noteId);
  }

  assume(): void {
    this.store.assignToMe();
  }

  retry(message: ThreadMessage): void {
    this.store.retryMessage(message.id);
  }

  protected quotedSenderLabel(sender: ThreadMessage['sender'] | undefined): string {
    return quotedReplySenderLabel(sender, this.conversation()?.contact);
  }

  /** "Responder" is WhatsApp-only: Meta's Instagram Send API has no reply/quote parameter, so
   *  quoting there would silently have no effect on the recipient's side — better to not offer
   *  the action than to let the agent believe they quoted something. */
  protected canReplyToMessages(): boolean {
    const conversation = this.conversation();
    return Boolean(conversation) && channelKindFromLabel(conversation!.channel) === 'whatsapp';
  }

  protected onReply(message: ThreadMessage): void {
    this.store.setReplyTarget(message);
  }

  /** Scrolls to and briefly highlights the quoted message when the user clicks a
   *  reply preview — mirrors WhatsApp's "jump to quoted message" behaviour. No-op
   *  when the message isn't currently rendered (outside the loaded page window). */
  scrollToMessage(messageId: string): void {
    const target = this.scroller()?.nativeElement.querySelector<HTMLElement>(`#message-${CSS.escape(messageId)}`);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.classList.add('message-bubble--highlight');
    setTimeout(() => target.classList.remove('message-bubble--highlight'), 1500);
  }

  protected statusLabel(status: ConversationView['status']): string {
    return conversationStatusLabel(status);
  }

  protected statusTone(status: ConversationView['status']): UiBadgeTone {
    if (status === 'open' || status === 'agent') return 'success';
    if (status === 'pending' || status === 'snoozed') return 'warning';
    if (status === 'channel_unavailable') return 'danger';
    return 'neutral';
  }

  protected delivery(message: ThreadMessage): DeliveryIndicator | null {
    return deliveryIndicator(message);
  }

  /** Tooltip do ⚠: o motivo instrutivo da falha (quando o backend o resolveu) + a dica de reenvio. */
  protected failureTitle(message: ThreadMessage): string {
    const reason = message.failureReason?.trim();
    return reason ? `${reason} Toque para reenviar.` : 'Falha ao enviar. Toque para reenviar.';
  }

  protected langfuseTraceUrl(message: ThreadMessage): string | null {
    if (!message.agentRunId || !environment.langfuseProjectId) {
      return null;
    }
    const traceId = message.agentRunId.replace(/-/g, '').toLowerCase();
    if (!/^[a-f0-9]{32}$/.test(traceId)) {
      return null;
    }
    const baseUrl = environment.langfuseBaseUrl.replace(/\/+$/g, '');
    return `${baseUrl}/project/${encodeURIComponent(environment.langfuseProjectId)}/traces/${traceId}`;
  }

  protected trackEntry(entry: TimelineEntry): string {
    if (entry.kind === 'note') {
      return `note-${entry.note.id}`;
    }
    return entry.message.id;
  }
}
